import { useState, useCallback, useMemo } from "react";
import { flattenItems } from "~/hooks/helpers";
import type { Item } from "~/types/api";
import { useFetchItems } from "../../hooks/apis/use-task-item";
import type { StatusGroup, TabWindow } from "../../task.types";
import { getTaskIdFromWindow } from "../utils/window-helpers";

type Props<T> = {
  setWindows: React.Dispatch<React.SetStateAction<TabWindow<T>[]>>;
  windowsRef: React.RefObject<TabWindow<T>[]>;
};

export const useDetailView = <T>({ setWindows, windowsRef }: Props<T>) => {
  const [detailViewItem, setDetailViewItem] = useState<Item[] | null>(null);
  const [activePanelWindowId, setActivePanelWindowId] = useState<string | null>(null);
  const [isLoadingDetailView, setIsLoadingDetailView] = useState(false);
  const { fetchMultipleItems } = useFetchItems();

  const updateWindowItemsData = useCallback(
    (windowId: string, freshItems: Item[]) => {
      setWindows((prev) =>
        prev.map((window) => {
          if (window.id === windowId) {
            const updatedItems = window.items.map((group) => {
              const g = group as StatusGroup;
              if (g.children) {
                return {
                  ...g,
                  children: g.children.map((item) => {
                    const freshItem = freshItems.find((fi) => fi.id === item.id);
                    return freshItem || item;
                  }),
                };
              }
              const freshItem = freshItems.find((fi) => fi.id === g.id);
              return freshItem || group;
            });

            const updatedOriginalItems = window.originalItems?.map((group) => {
              const g = group as StatusGroup;
              if (g.children) {
                return {
                  ...g,
                  children: g.children.map((item) => {
                    const freshItem = freshItems.find((fi) => fi.id === item.id);
                    return freshItem || item;
                  }),
                };
              }
              const freshItem = freshItems.find((fi) => fi.id === g.id);
              return freshItem || group;
            });

            return {
              ...window,
              items: updatedItems as T[],
              originalItems: updatedOriginalItems as T[],
            };
          }
          return window;
        })
      );
    },
    [setWindows]
  );

  const updateDetailViewForWindow = useCallback(
    async (windowId: string, newSelectedItems?: Set<number>, fetchFresh = false) => {
      const targetWindow = windowsRef.current.find((w) => w.id === windowId);
      if (!targetWindow) {
        setDetailViewItem(null);
        return;
      }

      const selectedItems = newSelectedItems || targetWindow.state.selectedItems;
      if (!selectedItems?.size) {
        setDetailViewItem(null);
        return;
      }

      const allItems = flattenItems(targetWindow.items as unknown[]) as Item[];
      const normalizedSelected = new Set([...selectedItems].map(Number));
      const localItems = allItems.filter((item) => normalizedSelected.has(Number(item.id)));

      if (localItems.length > 0) {
        setDetailViewItem(localItems);
      }

      if (fetchFresh && localItems.length > 0) {
        setIsLoadingDetailView(true);
        try {
          const taskId = getTaskIdFromWindow(windowId);

          if (taskId !== null) {
            const itemIds = [...selectedItems].map(Number);
            const freshItems = await fetchMultipleItems(taskId, itemIds);
            updateWindowItemsData(windowId, freshItems);
            setDetailViewItem(freshItems);
          } else {
            // Multi-task window: group selected items by their taskId and fetch per-task
            const itemsByTask = new Map<number, number[]>();
            for (const item of localItems) {
              const existing = itemsByTask.get(item.taskId) ?? [];
              existing.push(Number(item.id));
              itemsByTask.set(item.taskId, existing);
            }

            const freshResults = await Promise.all(
              Array.from(itemsByTask.entries()).map(([tId, ids]) => fetchMultipleItems(tId, ids))
            );
            const freshItems = freshResults.flat();
            if (freshItems.length > 0) {
              updateWindowItemsData(windowId, freshItems);
              setDetailViewItem(freshItems);
            }
          }
        } catch (error) {
          console.error("Failed to fetch fresh items:", error);
        } finally {
          setIsLoadingDetailView(false);
        }
      }

      if (localItems.length === 0) {
        setDetailViewItem(null);
      }
    },
    [fetchMultipleItems, updateWindowItemsData, windowsRef]
  );

  const togglePanelForWindow = useCallback(
    (windowId: string) => {
      setActivePanelWindowId((prev) => {
        if (prev === windowId) {
          return null;
        } else {
          updateDetailViewForWindow(windowId, undefined, true);
          return windowId;
        }
      });
    },
    [updateDetailViewForWindow]
  );

  const openDetailView = useCallback((item: Item | Item[]) => {
    setDetailViewItem(Array.isArray(item) ? item : [item]);
  }, []);

  const closeDetailView = useCallback(() => {
    setDetailViewItem(null);
    setActivePanelWindowId(null);
  }, []);

  return useMemo(
    () => ({
      detailViewItem,
      activePanelWindowId,
      isLoadingDetailView,
      setActivePanelWindowId,
      setDetailViewItem,
      updateDetailViewForWindow,
      togglePanelForWindow,
      openDetailView,
      closeDetailView,
    }),
    [
      detailViewItem,
      activePanelWindowId,
      isLoadingDetailView,
      updateDetailViewForWindow,
      togglePanelForWindow,
      openDetailView,
      closeDetailView,
    ]
  );
};
